import { supabase } from '../lib/supabase'
import type { Task, AITaskSuggestion } from '../types/task'
import type { Shoot, Client } from '../types'

export interface AIWorkflowTrigger {
  id: string
  name: string
  description: string
  triggerType: 'shoot_created' | 'shoot_completed' | 'client_added' | 'invoice_sent' | 'deadline_approaching'
  conditions: {
    shootType?: 'photo' | 'video'
    daysBeforeShoot?: number
    clientType?: string
    invoiceAmount?: number
  }
  actions: AITaskSuggestion[]
  isActive: boolean
  userId: string
}

export interface AIInsight {
  id: string
  type: 'productivity' | 'revenue' | 'efficiency' | 'workload'
  title: string
  description: string
  recommendation: string
  confidence: number
  data: any
  createdAt: string
}

class AIService {
  // Generate intelligent task suggestions based on context
  async generateTaskSuggestions(
    userId: string, 
    context: {
      shoot?: Shoot
      client?: Client
      recentTasks?: Task[]
      upcomingDeadlines?: Date[]
    }
  ): Promise<AITaskSuggestion[]> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      // Get user's task history to check if they have enough data for AI suggestions
      const { data: userTasks, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (error) throw error

      const taskCount = userTasks?.length || 0
      
      // Require minimum of 3 tasks before generating AI suggestions
      if (taskCount < 3) {
        return [{
          id: 'ai-learning-notice',
          title: 'AI Learning Mode',
          description: `Create ${3 - taskCount} more tasks to unlock AI-powered suggestions. The AI will analyze your workflow patterns and start generating intelligent task recommendations.`,
          priority: 'medium',
          category: 'admin',
          estimatedMinutes: 0,
          reasoning: 'AI needs more data to understand your workflow patterns and generate relevant suggestions.',
          confidence: 100,
          basedOn: 'learning',
          isLearningNotice: true
        }]
      }

      const suggestions: AITaskSuggestion[] = []

      // Shoot-based suggestions
      if (context.shoot) {
        const shootSuggestions = this.generateShootBasedTasks(context.shoot)
        suggestions.push(...shootSuggestions)
      }

      // Client-based suggestions
      if (context.client) {
        const clientSuggestions = this.generateClientBasedTasks(context.client)
        suggestions.push(...clientSuggestions)
      }

      // Pattern-based suggestions from user's actual tasks
      if (userTasks && userTasks.length >= 3) {
        const patternSuggestions = this.generatePatternBasedTasks(userTasks as Task[])
        suggestions.push(...patternSuggestions)
      }

      // Deadline-based suggestions
      if (context.upcomingDeadlines) {
        const deadlineSuggestions = this.generateDeadlineBasedTasks(context.upcomingDeadlines)
        suggestions.push(...deadlineSuggestions)
      }

      // Workflow-based suggestions from user's actual data
      if (userTasks && userTasks.length >= 5) {
        const workflowSuggestions = await this.generateWorkflowBasedTasks(userId, userTasks as Task[])
        suggestions.push(...workflowSuggestions)
      }

      return suggestions
    } catch (error) {
      console.error('Error generating AI suggestions:', error)
      return []
    }
  }

  // Generate tasks based on shoot details
  private generateShootBasedTasks(shoot: Shoot): AITaskSuggestion[] {
    const suggestions: AITaskSuggestion[] = []
    const shootDate = new Date(shoot.date)
    const now = new Date()
    const daysUntilShoot = Math.ceil((shootDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

    // Pre-shoot tasks
    if (daysUntilShoot > 0) {
      if (daysUntilShoot <= 7) {
        suggestions.push({
          id: `pre-shoot-${shoot.id}-equipment`,
          title: `Check equipment for ${shoot.title}`,
          description: `Verify all camera equipment, batteries, and memory cards are ready for the ${shoot.type} shoot`,
          priority: 'high',
          category: 'shoot_prep',
          estimatedMinutes: 30,
          reasoning: `Shoot is in ${daysUntilShoot} days - equipment check is critical`,
          confidence: 95,
          basedOn: 'shoot',
          relatedEntityId: shoot.id
        })

        suggestions.push({
          id: `pre-shoot-${shoot.id}-location`,
          title: `Scout location for ${shoot.title}`,
          description: `Visit and assess the shooting location, check lighting conditions and backup spots`,
          priority: 'medium',
          category: 'shoot_prep',
          estimatedMinutes: 90,
          reasoning: `Location scouting helps ensure smooth shoot execution`,
          confidence: 85,
          basedOn: 'shoot',
          relatedEntityId: shoot.id
        })
      }

      if (daysUntilShoot <= 3) {
        suggestions.push({
          id: `pre-shoot-${shoot.id}-client-confirm`,
          title: `Confirm details with ${shoot.client?.name}`,
          description: `Send confirmation email with time, location, and what to bring`,
          priority: 'urgent',
          category: 'client_communication',
          estimatedMinutes: 15,
          reasoning: `Final confirmation prevents no-shows and miscommunication`,
          confidence: 98,
          basedOn: 'shoot',
          relatedEntityId: shoot.id
        })
      }
    }

    // Post-shoot tasks
    if (daysUntilShoot < 0 && Math.abs(daysUntilShoot) <= 7) {
      suggestions.push({
        id: `post-shoot-${shoot.id}-backup`,
        title: `Backup photos from ${shoot.title}`,
        description: `Create secure backups of all raw files and organize folder structure`,
        priority: 'urgent',
        category: 'editing',
        estimatedMinutes: 45,
        reasoning: `Immediate backup prevents data loss`,
        confidence: 99,
        basedOn: 'shoot',
        relatedEntityId: shoot.id
      })

      suggestions.push({
        id: `post-shoot-${shoot.id}-preview`,
        title: `Send preview gallery to ${shoot.client?.name}`,
        description: `Select and edit 10-15 preview images to share with client`,
        priority: 'high',
        category: 'client_communication',
        estimatedMinutes: 120,
        reasoning: `Quick previews maintain client excitement and trust`,
        confidence: 90,
        basedOn: 'shoot',
        relatedEntityId: shoot.id
      })
    }

    return suggestions
  }

  // Generate tasks based on client patterns
  private generateClientBasedTasks(client: Client): AITaskSuggestion[] {
    const suggestions: AITaskSuggestion[] = []

    // New client onboarding
    suggestions.push({
      id: `client-${client.id}-welcome`,
      title: `Send welcome package to ${client.name}`,
      description: `Share client guide, contract templates, and portfolio examples`,
      priority: 'medium',
      category: 'client_communication',
      estimatedMinutes: 30,
      reasoning: `Professional onboarding improves client relationships`,
      confidence: 85,
      basedOn: 'client',
      relatedEntityId: client.id
    })

    return suggestions
  }

  // Generate tasks based on user patterns
  private generatePatternBasedTasks(recentTasks: Task[]): AITaskSuggestion[] {
    const suggestions: AITaskSuggestion[] = []

    // Analyze task completion patterns
    const editingTasks = recentTasks.filter(t => t.category === 'editing')
    const avgEditingTime = editingTasks.reduce((sum, t) => sum + (t.actualMinutes || 0), 0) / editingTasks.length

    if (avgEditingTime > 180) { // More than 3 hours average
      suggestions.push({
        id: 'pattern-editing-efficiency',
        title: 'Optimize editing workflow',
        description: 'Research and implement editing presets or batch processing techniques',
        priority: 'medium',
        category: 'admin',
        estimatedMinutes: 60,
        reasoning: `Your editing tasks average ${Math.round(avgEditingTime)} minutes - optimization could save time`,
        confidence: 75,
        basedOn: 'pattern'
      })
    }

    return suggestions
  }

  // Generate deadline-based tasks
  private generateDeadlineBasedTasks(deadlines: Date[]): AITaskSuggestion[] {
    const suggestions: AITaskSuggestion[] = []

    deadlines.forEach((deadline, index) => {
      const daysUntil = Math.ceil((deadline.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
      
      if (daysUntil <= 3 && daysUntil > 0) {
        suggestions.push({
          id: `deadline-reminder-${index}`,
          title: `Prepare for upcoming deadline`,
          description: `Review and finalize deliverables due in ${daysUntil} days`,
          priority: 'urgent',
          category: 'admin',
          estimatedMinutes: 45,
          reasoning: `Deadline approaching - preparation time needed`,
          confidence: 90,
          basedOn: 'deadline'
        })
      }
    })

    return suggestions
  }

  // Generate workflow-based tasks from user's actual task patterns
  private async generateWorkflowBasedTasks(_userId: string, userTasks: Task[]): Promise<AITaskSuggestion[]> {
    const suggestions: AITaskSuggestion[] = []

    // Analyze task completion patterns
    const completedTasks = userTasks.filter(t => t.status === 'completed')
    const pendingTasks = userTasks.filter(t => t.status === 'pending')

    // Find frequently used categories
    const categoryFrequency: { [key: string]: number } = {}
    userTasks.forEach(task => {
      categoryFrequency[task.category] = (categoryFrequency[task.category] || 0) + 1
    })

    const mostUsedCategory = Object.entries(categoryFrequency)
      .sort(([,a], [,b]) => b - a)[0]?.[0]

    // Suggest tasks based on patterns
    if (mostUsedCategory && pendingTasks.filter(t => t.category === mostUsedCategory).length === 0) {
      suggestions.push({
        id: `workflow-category-${mostUsedCategory}`,
        title: `Create ${mostUsedCategory.replace('_', ' ')} task`,
        description: `You frequently work on ${mostUsedCategory.replace('_', ' ')} tasks. Consider adding one for this week.`,
        priority: 'medium',
        category: mostUsedCategory as any,
        estimatedMinutes: 60,
        reasoning: `Based on your workflow patterns, you typically have ${mostUsedCategory.replace('_', ' ')} tasks`,
        confidence: 75,
        basedOn: 'workflow'
      })
    }

    // Suggest follow-up tasks for completed items
    const recentlyCompleted = completedTasks
      .filter(t => {
        const completedDate = new Date(t.completedAt || t.updatedAt)
        const daysSince = (new Date().getTime() - completedDate.getTime()) / (1000 * 60 * 60 * 24)
        return daysSince <= 7
      })
      .slice(0, 2)

    recentlyCompleted.forEach((task, index) => {
      if (task.category === 'editing') {
        suggestions.push({
          id: `workflow-followup-${task.id}-${index}`,
          title: `Deliver edited files for "${task.title}"`,
          description: `Follow up on the completed editing task by delivering final files to client`,
          priority: 'high',
          category: 'client_communication',
          estimatedMinutes: 30,
          reasoning: `Follow-up needed after completing editing task`,
          confidence: 85,
          basedOn: 'workflow',
          relatedEntityId: task.clientId || task.shootId
        })
      } else if (task.category === 'shoot_prep') {
        suggestions.push({
          id: `workflow-followup-${task.id}-${index}`,
          title: `Post-shoot backup and organization`,
          description: `Backup and organize files from recent shoot preparation`,
          priority: 'high',
          category: 'editing',
          estimatedMinutes: 45,
          reasoning: `Post-shoot organization typically follows shoot preparation`,
          confidence: 80,
          basedOn: 'workflow',
          relatedEntityId: task.shootId
        })
      }
    })

    return suggestions
  }

  // Create automated workflows
  async createWorkflow(userId: string, workflow: Omit<AIWorkflowTrigger, 'id' | 'userId'>): Promise<{ success: boolean; workflow?: AIWorkflowTrigger; error?: string }> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      const { data, error } = await supabase
        .from('ai_workflows')
        .insert({
          ...workflow,
          user_id: userId,
          created_at: new Date().toISOString()
        })
        .select()
        .single()

      if (error) throw error

      return { success: true, workflow: data }
    } catch (error) {
      console.error('Error creating workflow:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  }

  // Get user's active workflows
  async getWorkflows(userId: string): Promise<AIWorkflowTrigger[]> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      const { data, error } = await supabase
        .from('ai_workflows')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching workflows:', error)
      return []
    }
  }

  // Generate AI insights about user's business
  async generateInsights(userId: string): Promise<AIInsight[]> {
    const insights: AIInsight[] = []

    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      // Get user's data for analysis
      const [tasksResult, invoicesResult] = await Promise.all([
        supabase.from('tasks').select('*').eq('user_id', userId),
        supabase.from('invoices').select('*').eq('user_id', userId)
      ])

      const tasks = tasksResult.data || []
      const invoices = invoicesResult.data || []

      // Productivity insights
      const completedTasks = tasks.filter(t => t.status === 'completed')
      const completionRate = tasks.length > 0 ? (completedTasks.length / tasks.length) * 100 : 0

      if (completionRate < 70) {
        insights.push({
          id: 'productivity-low-completion',
          type: 'productivity',
          title: 'Task Completion Rate Below Average',
          description: `Your current task completion rate is ${Math.round(completionRate)}%`,
          recommendation: 'Consider breaking large tasks into smaller, manageable chunks and setting realistic deadlines',
          confidence: 85,
          data: { completionRate, totalTasks: tasks.length, completedTasks: completedTasks.length },
          createdAt: new Date().toISOString()
        })
      }

      // Revenue insights
      const thisMonth = new Date()
      const monthlyInvoices = invoices.filter(inv => {
        const invDate = new Date(inv.created_at)
        return invDate.getMonth() === thisMonth.getMonth() && invDate.getFullYear() === thisMonth.getFullYear()
      })

      const monthlyRevenue = monthlyInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0)

      if (monthlyRevenue > 0) {
        insights.push({
          id: 'revenue-monthly-summary',
          type: 'revenue',
          title: 'Monthly Revenue Performance',
          description: `You've generated $${monthlyRevenue.toFixed(2)} this month from ${monthlyInvoices.length} invoices`,
          recommendation: 'Consider upselling additional services or increasing rates for premium packages',
          confidence: 95,
          data: { monthlyRevenue, invoiceCount: monthlyInvoices.length },
          createdAt: new Date().toISOString()
        })
      }

      // Efficiency insights
      const avgTaskTime = completedTasks.reduce((sum, t) => sum + (t.actual_minutes || 0), 0) / completedTasks.length
      const avgEstimatedTime = completedTasks.reduce((sum, t) => sum + (t.estimated_minutes || 0), 0) / completedTasks.length

      if (avgTaskTime > avgEstimatedTime * 1.5) {
        insights.push({
          id: 'efficiency-time-estimation',
          type: 'efficiency',
          title: 'Time Estimation Accuracy',
          description: 'Tasks are taking longer than estimated on average',
          recommendation: 'Review your time estimation process and add buffer time for complex tasks',
          confidence: 80,
          data: { avgActual: avgTaskTime, avgEstimated: avgEstimatedTime },
          createdAt: new Date().toISOString()
        })
      }

      return insights
    } catch (error) {
      console.error('Error generating insights:', error)
      return []
    }
  }

  // Smart task prioritization
  async prioritizeTasks(tasks: Task[]): Promise<Task[]> {
    return tasks.sort((a, b) => {
      // Priority weights
      const priorityWeights = { urgent: 4, high: 3, medium: 2, low: 1 }
      
      // Due date urgency
      const now = new Date()
      const aDaysUntilDue = a.dueDate ? Math.ceil((new Date(a.dueDate).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : 999
      const bDaysUntilDue = b.dueDate ? Math.ceil((new Date(b.dueDate).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : 999
      
      // Calculate scores
      const aScore = priorityWeights[a.priority] + (aDaysUntilDue < 0 ? 10 : Math.max(0, 10 - aDaysUntilDue))
      const bScore = priorityWeights[b.priority] + (bDaysUntilDue < 0 ? 10 : Math.max(0, 10 - bDaysUntilDue))
      
      return bScore - aScore
    })
  }

  // Auto-schedule tasks based on availability and priorities
  async autoScheduleTasks(tasks: Task[], workingHours: { start: number; end: number }): Promise<Task[]> {
    const scheduledTasks = [...tasks]
    const now = new Date()
    
    // Sort by priority and due date
    const prioritizedTasks = await this.prioritizeTasks(scheduledTasks)
    
    // Simple auto-scheduling logic
    let currentDate = new Date(now)
    let currentHour = Math.max(workingHours.start, now.getHours())
    
    prioritizedTasks.forEach(task => {
      if (!task.dueDate && task.estimatedMinutes) {
        const hoursNeeded = Math.ceil(task.estimatedMinutes / 60)
        
        // Check if task fits in current day
        if (currentHour + hoursNeeded <= workingHours.end) {
          // Schedule for current day
          task.dueDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), currentHour + hoursNeeded).toISOString()
          currentHour += hoursNeeded
        } else {
          // Move to next day
          currentDate.setDate(currentDate.getDate() + 1)
          currentHour = workingHours.start + hoursNeeded
          task.dueDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), currentHour).toISOString()
        }
        
        task.autoScheduled = true
      }
    })
    
    return prioritizedTasks
  }
}

export const aiService = new AIService() 